import React, { Fragment, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";

import { RiskService } from "services/RiskService";
import { FormSlider, FormSwitch } from "components/form-fields";
import { Numeric } from "components/formatting";
import { ShowHide } from "components/layout";
import { Button, ButtonSave } from "components/buttons";
import { AlertUnsavedData, Modal, ModalSize, NotificationInline, NotificationToaster } from "components/notifications";
import { Tooltip } from "components/tooltip";
import { Icon, Intent } from "@blueprintjs/core";
import { useDirtyData } from "hooks/useDirtyData";

export function RiskCarryOverSettings(props) {
    const [pointsCarryOver, setPointsCarryOver] = useState({});
    const [modifiedPointsCarryOver, setModifiedPointsCarryOver] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingPointsCarryOverChanges, setPendingPointsCarryOverChanges] = useState(false);
    const effectivePointsCarryOver = useMemo(() => ({
        numberOfPeriods: pendingPointsCarryOverChanges ? pointsCarryOver.pendingNumberOfPeriods : pointsCarryOver.numberOfPeriods,
        configureCarryOver: pendingPointsCarryOverChanges ? pointsCarryOver.pendingConfigureCarryOver : pointsCarryOver.configureCarryOver
    }), [pendingPointsCarryOverChanges, pointsCarryOver]);

    const isDataDirty = useDirtyData(effectivePointsCarryOver, modifiedPointsCarryOver);
    const [modalPointsCarryOver, setModalPointsCarryOver] = useState(null);

    useEffect(init, []);

    function init() {
        setLoading(true);
        setModifiedPointsCarryOver({});

        RiskService.getPointsCarryOver().then(
            function (pointsCarryOverResponse) {
                setPointsCarryOver(pointsCarryOverResponse);
                setPendingPointsCarryOverChanges(pointsCarryOverResponse.pendingConfigureCarryOver !== null || pointsCarryOverResponse.pendingNumberOfPeriods !== null);
                setLoading(false);
            },
            function () {
                NotificationToaster.show(Intent.DANGER, "Settings could not be loaded. Please try loading the page again");
            }
        );
    }
    function onSavePointsCarryOver() {
        setSaving(true);

        RiskService.savePointsCarryOver({
            ...pointsCarryOver,
            ...modifiedPointsCarryOver,
        }).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Settings saved successfully");
            init();
        }).catch((error) => {
            NotificationToaster.show(Intent.DANGER, error);
        }).finally(() => {
            setSaving(false);
            setModifiedPointsCarryOver({});
        });
    }

    function showPendingInfo(carryOver) {
        setModalPointsCarryOver(carryOver);
    }

    function onModalDismiss() {
        setModalPointsCarryOver(null);
    }

    const getConfigureCarryOver = () =>
        modifiedPointsCarryOver.configureCarryOver ?? (pendingPointsCarryOverChanges ? pointsCarryOver.pendingConfigureCarryOver : pointsCarryOver.configureCarryOver);

    const getNumberOfPeriods = () =>
        modifiedPointsCarryOver.numberOfPeriods ?? (pendingPointsCarryOverChanges ? pointsCarryOver.pendingNumberOfPeriods : pointsCarryOver.numberOfPeriods);

    const pluralisation = () => getNumberOfPeriods() - 1 === 1 ? "" : "s";

    return (
        <>
            <h2>Points Carry-Over Settings</h2>
            <NotificationInline
                intent="primary"
                show={!loading && pendingPointsCarryOverChanges}
                allowClose={false}
                text={(
                    <span>
                        There are saved pending changes to your points carry-over settings. They take effect at the start of your next risk period, when all of your scores, including those from previous periods, are recalculated to reflect them. Click the &nbsp;<Icon icon="time" />&nbsp; icon for more information.
                    </span>
                )}
            />
            <div className="inline-items spacer-bottom">
                <FormSwitch
                    id="settings-enabled"
                    loading={loading}
                    disabled={saving}
                    headingText={
                        <span className="risk-points-carry-over-tooltip">
                            Configure Points Carry-Over
                            <div className="spacer-left">
                                <ShowHide
                                    evaluator={pendingPointsCarryOverChanges}
                                    show={(
                                        <Tooltip content="Points carry-over settings have a pending change, click for more info">
                                            <Button onClick={() => showPendingInfo(pendingPointsCarryOverChanges)} icon="time" iconOnly={true} large={false} minimal={true}></Button>
                                        </Tooltip>
                                    )}
                                />
                            </div>
                        </span>
                    }
                    checked={getConfigureCarryOver()}
                    onChange={() =>
                        setModifiedPointsCarryOver({
                            ...modifiedPointsCarryOver,
                            configureCarryOver: !getConfigureCarryOver()
                        })
                    }
                />
            </div>
            <ShowHide
                evaluator={getConfigureCarryOver()}
                show={
                    <Fragment>
                        <FormSlider
                            min={1}
                            max={10}
                            stepSize={1}
                            labelStepSize={9}
                            value={getNumberOfPeriods()}
                            onChange={(value) =>
                                setModifiedPointsCarryOver({
                                    ...modifiedPointsCarryOver,
                                    numberOfPeriods: value
                                })
                            }
                            showLabels
                            leftLabelHeader="Number of periods"
                            leftLabelValue={<Numeric>{getNumberOfPeriods()}</Numeric>}
                            isLoading={loading}
                            isSaving={saving}
                        />
                        <p className="spacer-top">
                            {getNumberOfPeriods() > 1
                                ? `After the period in which the points occurred, points will be carried over to the next ${getNumberOfPeriods() - 1}
                                period${pluralisation()}. Carried-over points will have a diminishing impact on subsequent periods, with their weight decreasing proportionally from the period in which they occurred.`
                                : `Points will not be carried over to the next period.`}
                        </p>
                    </Fragment>
                }
                hide={<p>All Points will be carried over until 10 periods have passed since the points occurred.</p>}
            />
            <NotificationInline allowClose={false} intent="warning" text="Because a risk period is currently in progress, your changes won't be applied yet. They take effect at the start of your next risk period, when all of your scores, including those from previous periods, are recalculated to reflect them." show={props.riskStarted && isDataDirty} />
            <AlertUnsavedData isDirty={isDataDirty}></AlertUnsavedData>
            <div className="spacer-top">
                <ButtonSave
                    onClick={onSavePointsCarryOver}
                    disabled={saving}
                    simpleDisabled={!isDataDirty}
                    loading={loading}
                    />
            </div>
            <Modal
                isOpen={modalPointsCarryOver !== null}
                title={"Points Carry-Over Changes"}
                onClose={onModalDismiss}
                size={ModalSize.SMALL}
            >
                <p>The following changes will take place from the start of your next risk period:</p>
                <div className="inline-items spacer-bottom-small">
                    <div className="inline-item"><strong>Configure Carry-Over:</strong></div>
                    <div className="inline-item">{pointsCarryOver.configureCarryOver ? "Enabled" : "Disabled"}</div>
                    <div className="inline-item"><Icon icon="arrow-right" /></div>
                    <div className="inline-item">{pointsCarryOver?.pendingConfigureCarryOver ? "Enabled" : "Disabled"}</div>
                </div>
                <div className="inline-items">
                    <div className="inline-item"><strong>Number of Periods:</strong></div>
                    <div className="inline-item">{pointsCarryOver.numberOfPeriods}</div>
                    <div className="inline-item"><Icon icon="arrow-right" /></div>
                    <div className="inline-item">{pointsCarryOver?.pendingNumberOfPeriods}</div>
                </div>
            </Modal>
        </>
    );
}

RiskCarryOverSettings.propTypes = {
    riskStarted: PropTypes.bool.isRequired
};